import { AttachmentFile } from '@billy/design-system'
import { trackError } from '@billy/track-error'

import { parseAttachmentDataJson } from '@features/eInvoicing/utils/parseAttachmentDataJson'
import { VoucherFile } from '@modules-deprecated/inbox/types'

import { AttachmentParsedData } from '../../../../../types/attachmentParsedData'
import { BohrScanResult } from '../../../../../types/bohrScanResult'
import { EInvoiceParseResult } from '../../../../../types/eInvoiceParseResult'

const isBohrScanResult = (dataJson: unknown): dataJson is BohrScanResult =>
  // typeof null is 'object'
  dataJson !== null &&
  typeof dataJson === 'object' &&
  (Object.prototype.hasOwnProperty.call(dataJson, 'entities') ||
    Object.prototype.hasOwnProperty.call(dataJson, 'supplier'))

const isEInvoiceParseResult = (dataJson: unknown): dataJson is EInvoiceParseResult =>
  // typeof null is 'object'
  dataJson !== null &&
  typeof dataJson === 'object' &&
  Object.prototype.hasOwnProperty.call(dataJson, 'invoiceId') &&
  Object.prototype.hasOwnProperty.call(dataJson, 'format')

const formatBohrScanResult = (data: BohrScanResult): AttachmentParsedData => {
  const {
    document_issue_date: documentIssueDate,
    document_due_date: documentDueDate,
    document_total_incl_vat: documentTotalInclVat,
    document_currency: documentCurrency,
    supplier,
  } = data

  const {
    issue_date: rawIssueDate,
    due_date: rawDueDate,
    total_incl_vat: rawTotalInclVat,
    total_vat_amount: rawTotalVatAmount,
    currency: currencyEntity,
    vendor: vendorEntity,
    vat_summary: vatSummaryEntity,
  } = data.entities ?? {}
  const rawTagScores = data.tag_scores ?? []

  const issuedDate =
    (rawIssueDate?.content && new Date(rawIssueDate.content)) ||
    (documentIssueDate && new Date(documentIssueDate)) ||
    null
  const dueDate =
    (rawDueDate?.content && new Date(rawDueDate.content)) || (documentDueDate && new Date(documentDueDate)) || null

  const totalInclVat = rawTotalInclVat?.content !== undefined ? rawTotalInclVat?.content : documentTotalInclVat
  const totalVat = rawTotalVatAmount?.content ?? null

  const tagScores =
    rawTagScores.map((tagScore) => ({
      tagId: tagScore.tag_id,
      tagName: tagScore.tag_name,
      score: tagScore.score,
    })) || []

  const summaryEntities = Array.isArray(vatSummaryEntity) ? vatSummaryEntity : vatSummaryEntity?.content || []

  return {
    source: 'bohr',
    amounts: {
      totalInclVat,
      totalVat,
    },
    currency: currencyEntity?.content || documentCurrency || null,
    dates: {
      issued: issuedDate,
      due: dueDate,
    },
    vendor: {
      name: vendorEntity?.content?.name || supplier?.name || null,
      phone: vendorEntity?.content?.phone || supplier?.phone || null,
      registrationNo: vendorEntity?.content?.registration_no || supplier?.registration_no || null,
      vatNo: vendorEntity?.content?.vat_no || supplier?.vat_no || null,
      address: {
        country: vendorEntity?.content?.country || supplier?.country || null,
        city: vendorEntity?.content?.address_city || supplier?.address_city || null,
        street: vendorEntity?.content?.address_street || supplier?.address_street || null,
        zip: vendorEntity?.content?.address_zip || supplier?.address_zip || null,
      },
    },
    tagScores,
    vatSummary:
      summaryEntities?.map((item) => ({
        amountInclVat: item?.amount_incl_vat || null,
        amount: item?.amount || null,
        rate: item?.rate || null,
        vat: item?.vat || null,
      })) || [],
  }
}

const formatEInvoiceParseResult = (data: EInvoiceParseResult): AttachmentParsedData => {
  const {
    amounts: { total: totalInclVat, vat: totalVat },
    currency,
    dates: { issued: issuedDate, due: dueDate },
    vendor,
  } = data

  return {
    source: 'e-invoicing',
    amounts: {
      totalInclVat,
      totalVat,
    },
    currency,
    dates: {
      issued: new Date(issuedDate),
      due: dueDate ? new Date(dueDate) : null,
    },
    vendor: {
      name: vendor.name,
      phone: null,
      registrationNo: vendor.registrationNo,
      vatNo: vendor.vatNo,
      address: {
        country: vendor.address.country,
        city: vendor.address.city,
        street: vendor.address.street,
        zip: vendor.address.zip,
      },
    },
    tagScores: [],
    vatSummary: [],
  }
}

/**
 * Parses the JSON data of attachments and returns a fixed set of data used to pre-fill various form fields.
 *
 * The JSON data can either be populated by a Bohr scan or an e-invoicing-service parse, thus this helper abstracts
 * that parsing logic to prevent leaking it to the consumers.
 */
export const getParsedDataFromAttachment = (
  attachmentFile: AttachmentFile | VoucherFile | undefined,
): AttachmentParsedData | undefined => {
  if (!attachmentFile?.dataJson) {
    return undefined
  }

  const data = parseAttachmentDataJson(attachmentFile.dataJson)

  if (!data) {
    return undefined
  }

  try {
    if (isEInvoiceParseResult(data)) {
      return formatEInvoiceParseResult(data)
    }

    if (isBohrScanResult(data) && (data._success === undefined || data._success === true)) {
      return formatBohrScanResult(data)
    }

    return undefined
  } catch (error) {
    trackError(error)
    return undefined
  }
}
